import { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, Cell, ReferenceLine,
} from 'recharts';
import { HL_WALLET, hlPost } from '../hyperliquid';
import { tradeResult } from '../tradePnl';
import './Report.css';

function sigMs(sig) {
  return sig.timestamp?.toDate ? sig.timestamp.toDate().getTime()
       : sig.timestamp ? new Date(sig.timestamp).getTime() : 0;
}

function useReportData() {
  const [fills,   setFills]   = useState([]);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    Promise.all([
      hlPost({ type: 'userFills', user: HL_WALLET }),
      getDocs(query(collection(db, 'signals'), orderBy('timestamp', 'asc'))),
    ])
      .then(([fillsData, snap]) => {
        setFills(Array.isArray(fillsData) ? fillsData : []);
        setSignals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  return { fills, signals, loading, error };
}

// ── Build one trade record per SC signal ──────────────────────────────────────
// Each record aggregates ALL fills that belong to that signal's window.
// closedPnl = sum of closedPnl from all closing fills in the window.
// This guarantees: total trades = number of SC signals (~21), not fill count.
function buildTradeRecords(signals, fills) {
  const scSignals = signals
    .filter(s => s.tradeId && s.status !== 'duplicate_skipped' && s.action !== 'CLOSE')
    .sort((a, b) => sigMs(a) - sigMs(b));

  return scSignals.map(sig => {
    const coin      = (sig.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
    const direction = (sig.direction || '').toUpperCase();
    const dirLabel  = direction === 'LONG' ? 'Long' : 'Short';
    const startMs   = sigMs(sig);

    // next SC signal for same coin → defines end of this trade's fill window
    const nextSig = scSignals.find(s => {
      const c = (s.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
      return c === coin && sigMs(s) > startMs;
    });
    const endMs = nextSig ? sigMs(nextSig) : Infinity;

    const tradeFills  = fills.filter(f =>
      f.coin === coin &&
      (f.dir || '').includes(dirLabel) &&
      f.time >= startMs &&
      f.time < endMs
    );
    const closeFills  = tradeFills.filter(f => (f.dir || '').includes('Close'));
    const fee         = tradeFills.reduce((s, f) => s + parseFloat(f.fee || 0), 0);
    const lastClose   = closeFills[closeFills.length - 1];
    const exitMs      = lastClose ? lastClose.time : null;
    const isOpen      = closeFills.length === 0;

    const hl = tradeResult(sig, fills, nextSig ? sigMs(nextSig) : null);
    return {
      tradeId:   sig.tradeId,
      coin,
      direction,
      strategy:  sig.strategy || sig.source || null,
      startMs,
      exitMs,
      closedPnl: hl ? hl.net : null,
      fee:       hl ? hl.fees : fee,
      isOpen:    hl ? false : isOpen,
    };
  });
}

// ── Stats from trade records (one per SC signal) ──────────────────────────────
function computeStats(records) {
  const closed = records.filter(r => !r.isOpen && r.closedPnl !== null);
  if (closed.length === 0) return null;

  const wins   = closed.filter(r => r.closedPnl > 0);
  const losses = closed.filter(r => r.closedPnl < 0);

  const totalPnl    = closed.reduce((s, r) => s + r.closedPnl, 0);
  const grossWins   = wins.reduce((s, r) => s + r.closedPnl, 0);
  const grossLosses = Math.abs(losses.reduce((s, r) => s + r.closedPnl, 0));
  const profitFactor = grossLosses > 0 ? grossWins / grossLosses : null;
  const avgWin      = wins.length   > 0 ? grossWins   / wins.length   : 0;
  const avgLoss     = losses.length > 0 ? grossLosses / losses.length : 0;
  const totalFees   = records.reduce((s, r) => s + r.fee, 0);

  const longClosed  = closed.filter(r => r.direction === 'LONG');
  const shortClosed = closed.filter(r => r.direction === 'SHORT');
  const sideWinRate = arr =>
    arr.length > 0 ? (arr.filter(r => r.closedPnl > 0).length / arr.length) * 100 : null;

  const dailyMap = {};
  [...closed].sort((a, b) => a.exitMs - b.exitMs).forEach(r => {
    const day = new Date(r.exitMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    dailyMap[day] = (dailyMap[day] || 0) + r.closedPnl;
  });
  const dailyData = Object.entries(dailyMap).map(([day, pnl]) => ({ day, pnl: +pnl.toFixed(2) }));

  let cum = 0;
  const cumulativeData = [...closed].sort((a, b) => a.exitMs - b.exitMs).map(r => {
    cum += r.closedPnl;
    return {
      time: new Date(r.exitMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      pnl: +cum.toFixed(2),
    };
  });

  const byPnl = [...closed].sort((a, b) => b.closedPnl - a.closedPnl);
  const best  = byPnl.slice(0, 3);
  const worst = byPnl.slice(-2).reverse();

  return {
    totalTrades: closed.length,
    wins: wins.length, losses: losses.length,
    winRate: (wins.length / closed.length) * 100,
    totalPnl, profitFactor, avgWin, avgLoss, totalFees,
    longClosed: longClosed.length, shortClosed: shortClosed.length,
    longWinRate: sideWinRate(longClosed), shortWinRate: sideWinRate(shortClosed),
    dailyData, cumulativeData, best, worst,
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtPnl(v) {
  const n = Number(v);
  if (n === 0) return '$0.00';
  return `${n > 0 ? '+' : '-'}$${Math.abs(n).toFixed(2)}`;
}

function toDateInput(d) { return d.toISOString().slice(0, 10); }

const TT_STYLE = {
  background: '#1a1a1e', border: '1px solid #2a2a2e',
  borderRadius: 6, fontSize: 12, color: '#e8eaf0',
};

// ── Sub-components ────────────────────────────────────────────────────────────
function RCard({ label, value, sub, color }) {
  return (
    <div className="rp-stat-card">
      <div className="rp-stat-label">{label}</div>
      <div className="rp-stat-value" style={color ? { color } : undefined}>{value}</div>
      {sub && <div className="rp-stat-sub">{sub}</div>}
    </div>
  );
}

function WinBar({ label, pct, count, isLong }) {
  const color = isLong ? '#00c896' : '#e53e3e';
  return (
    <div className="rp-win-bar">
      <div className="rp-win-bar-header">
        <span className={`rp-side-badge rp-side-badge--${isLong ? 'long' : 'short'}`}>{label}</span>
        <span className="rp-win-pct" style={{ color }}>
          {pct != null ? `${pct.toFixed(1)}%` : 'N/A'}
        </span>
        <span className="rp-win-count">{count} trades</span>
      </div>
      {pct != null && (
        <div className="rp-win-track">
          <div className="rp-win-fill" style={{ width: `${pct}%`, background: color }} />
        </div>
      )}
    </div>
  );
}

function TradeRow({ record, rank }) {
  const pnl    = record.closedPnl ?? 0;
  const isWin  = pnl > 0;
  const isLong = record.direction === 'LONG';
  const color  = isWin ? '#00c896' : '#e53e3e';
  return (
    <div className="rp-trade-row">
      <span className="rp-trade-rank" style={{ color }}>#{rank}</span>
      <span className="rp-trade-coin">{record.coin}</span>
      <span className={`rp-side-badge rp-side-badge--${isLong ? 'long' : 'short'}`}>
        {isLong ? 'LONG' : 'SHORT'}
      </span>
      <span className="rp-trade-pnl" style={{ color }}>{fmtPnl(pnl)}</span>
      <span className="rp-trade-date">
        {record.exitMs ? new Date(record.exitMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
      </span>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
function Report() {
  const { fills, signals, loading, error } = useReportData();

  const [sideFilter,     setSideFilter]     = useState('ALL');
  const [coinFilter,     setCoinFilter]     = useState('ALL');
  const [strategyFilter, setStrategyFilter] = useState('ALL');
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');

  // One record per SC signal — the ground truth
  const allRecords = useMemo(() => buildTradeRecords(signals, fills), [signals, fills]);

  // Filter options
  const availableCoins = useMemo(() => {
    const set = new Set(allRecords.map(r => r.coin).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [allRecords]);

  const availableStrategies = useMemo(() => {
    const set = new Set(allRecords.map(r => r.strategy).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [allRecords]);

  // Apply filters
  const filteredRecords = useMemo(() => {
    return allRecords.filter(r => {
      if (coinFilter !== 'ALL' && r.coin !== coinFilter) return false;
      if (sideFilter !== 'ALL') {
        if (sideFilter === 'LONG'  && r.direction !== 'LONG')  return false;
        if (sideFilter === 'SHORT' && r.direction !== 'SHORT') return false;
      }
      if (strategyFilter !== 'ALL' && r.strategy !== strategyFilter) return false;
      if (dateFrom && r.startMs < new Date(dateFrom).getTime()) return false;
      if (dateTo   && r.startMs > new Date(dateTo + 'T23:59:59').getTime()) return false;
      return true;
    });
  }, [allRecords, coinFilter, sideFilter, strategyFilter, dateFrom, dateTo]);

  const stats = useMemo(() => computeStats(filteredRecords), [filteredRecords]);

  function clearFilters() {
    setSideFilter('ALL');
    setCoinFilter('ALL');
    setStrategyFilter('ALL');
    setDateFrom('');
    setDateTo('');
  }

  const hasFilter = sideFilter !== 'ALL' || coinFilter !== 'ALL' || strategyFilter !== 'ALL' || dateFrom || dateTo;

  function downloadCSV() {
    const closed = filteredRecords.filter(r => !r.isOpen);
    if (closed.length === 0) return;
    const headers = ['Trade ID', 'Date', 'Coin', 'Side', 'PnL', 'Fee', 'Strategy'];
    const rows = closed.map(r => [
      r.tradeId,
      r.exitMs ? new Date(r.exitMs).toISOString().slice(0, 19).replace('T', ' ') : '',
      r.coin,
      r.direction,
      r.closedPnl != null ? r.closedPnl.toFixed(4) : '',
      r.fee.toFixed(4),
      r.strategy || '',
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'report.csv' });
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return <div className="loading-state"><div className="spinner" /><p>Loading trade history…</p></div>;
  }

  if (error) {
    return <div className="rp-empty">Error loading data: {error}</div>;
  }

  const pnlColor = stats ? (stats.totalPnl >= 0 ? '#00c896' : '#e53e3e') : '#00c896';

  return (
    <div className="rp-page">
      <div className="page-header">
        <div className="page-header-main"><h1>Report</h1></div>
        <p>Hyperliquid trade analytics · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>

      {/* ── Filter bar ───────────────────────────────────────────────────── */}
      <div className="rp-toolbar card">
        <div className="rp-filter-group">
          <label className="rp-filter-label">Side</label>
          <div className="rp-side-btns">
            {['ALL', 'LONG', 'SHORT'].map(side => (
              <button key={side}
                className={`rp-side-btn rp-side-btn--${side.toLowerCase()}${sideFilter === side ? ' active' : ''}`}
                onClick={() => setSideFilter(side)}>
                {side}
              </button>
            ))}
          </div>
        </div>

        <div className="rp-filter-group">
          <label className="rp-filter-label">Coin</label>
          <select className="rp-filter-select" value={coinFilter} onChange={e => setCoinFilter(e.target.value)}>
            {availableCoins.map(c => (
              <option key={c} value={c}>{c === 'ALL' ? 'All Coins' : c}</option>
            ))}
          </select>
        </div>

        {availableStrategies.length > 2 && (
          <div className="rp-filter-group">
            <label className="rp-filter-label">Strategy</label>
            <select className="rp-filter-select" value={strategyFilter} onChange={e => setStrategyFilter(e.target.value)}>
              {availableStrategies.map(s => (
                <option key={s} value={s}>{s === 'ALL' ? 'All Strategies' : s}</option>
              ))}
            </select>
          </div>
        )}

        <div className="rp-filter-group">
          <label className="rp-filter-label">From</label>
          <input type="date" className="rp-filter-select" value={dateFrom}
            max={dateTo || toDateInput(new Date())} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="rp-filter-group">
          <label className="rp-filter-label">To</label>
          <input type="date" className="rp-filter-select" value={dateTo}
            min={dateFrom} max={toDateInput(new Date())} onChange={e => setDateTo(e.target.value)} />
        </div>

        <div className="rp-toolbar-right">
          {hasFilter && <button className="rp-clear-btn" onClick={clearFilters}>Clear</button>}
          <button className="rp-csv-btn" onClick={downloadCSV} disabled={!stats}>↓ CSV</button>
          {stats && (
            <span className="rp-filter-count">{stats.totalTrades} trade{stats.totalTrades !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {/* ── No data state ────────────────────────────────────────────────── */}
      {!stats && (
        <div className="rp-empty">
          {hasFilter ? 'No closed trades match the selected filters.' : 'No closed trades on this account yet.'}
        </div>
      )}

      {stats && (<>

        {/* Row 1 — 4 cards */}
        <div className="rp-grid rp-grid--4">
          <RCard label="Win Rate"
            value={`${stats.winRate.toFixed(1)}%`}
            sub={`${stats.wins}W · ${stats.losses}L`}
            color={stats.winRate >= 50 ? '#00c896' : '#e53e3e'} />
          <RCard label="Total PnL"
            value={fmtPnl(stats.totalPnl)}
            sub="After fees"
            color={pnlColor} />
          <RCard label="Profit Factor"
            value={stats.profitFactor != null ? stats.profitFactor.toFixed(2) : '—'}
            sub="Gross wins ÷ gross losses"
            color={stats.profitFactor != null && stats.profitFactor >= 1 ? '#00c896' : undefined} />
          <RCard label="Total Trades"
            value={stats.totalTrades}
            sub={`${stats.longCloses} long · ${stats.shortCloses} short`} />
        </div>

        {/* Row 2 — 3 cards */}
        <div className="rp-grid rp-grid--3">
          <RCard label="Avg Win"  value={`+$${stats.avgWin.toFixed(2)}`}  sub="Per winning trade" color="#00c896" />
          <RCard label="Avg Loss" value={`-$${stats.avgLoss.toFixed(2)}`} sub="Per losing trade"  color="#e53e3e" />
          <RCard label="Total Fees" value={`-$${stats.totalFees.toFixed(2)}`} sub="Already inside Total PnL" color="#e53e3e" />
        </div>

        {/* Row 3 — 2 charts */}
        <div className="rp-grid rp-grid--2">
          <div className="rp-chart-card">
            <div className="rp-chart-title">Daily PnL</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.dailyData} margin={{ top: 8, right: 4, left: -16, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fill: '#8b8b9a', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8b8b9a', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={TT_STYLE} formatter={v => [fmtPnl(v), 'PnL']} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <ReferenceLine y={0} stroke="#2a2a2e" />
                <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>
                  {stats.dailyData.map((e, i) => (
                    <Cell key={i} fill={e.pnl >= 0 ? '#00c896' : '#e53e3e'} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rp-chart-card">
            <div className="rp-chart-title">Cumulative PnL</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={stats.cumulativeData} margin={{ top: 8, right: 4, left: -16, bottom: 0 }}>
                <XAxis dataKey="time" tick={{ fill: '#8b8b9a', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8b8b9a', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={TT_STYLE} formatter={v => [fmtPnl(v), 'Cumulative']} />
                <ReferenceLine y={0} stroke="#2a2a2e" />
                <Line type="monotone" dataKey="pnl" stroke={pnlColor}
                  strokeWidth={2} dot={false} activeDot={{ r: 4, fill: pnlColor }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Row 4 — win rate by side + best/worst trades */}
        <div className="rp-grid rp-grid--2">
          <div className="rp-card">
            <div className="rp-card-title">Win Rate by Side</div>
            <div className="rp-win-bars">
              <WinBar label="LONG"  pct={stats.longWinRate}  count={stats.longClosed}  isLong={true} />
              <WinBar label="SHORT" pct={stats.shortWinRate} count={stats.shortClosed} isLong={false} />
            </div>
          </div>

          <div className="rp-card">
            <div className="rp-card-title">Best &amp; Worst Trades</div>
            <div className="rp-trade-list">
              <div className="rp-trade-section-label">Best</div>
              {stats.best.map((r, i) => <TradeRow key={r.tradeId ?? `b${i}`} record={r} rank={i + 1} />)}
              <div className="rp-trade-divider" />
              <div className="rp-trade-section-label">Worst</div>
              {stats.worst.map((r, i) => <TradeRow key={r.tradeId ?? `w${i}`} record={r} rank={i + 1} />)}
            </div>
          </div>
        </div>

      </>)}
    </div>
  );
}

export default Report;
